import type AHDate from "_shared/dates/date";

/**
 * The time a message was sent, and nothing else: "2:52 PM" (#7082). The day is the divider's to say,
 * once above every message sent on it — see `conversationDayLabel`. A weekday or a date on every bubble
 * repeated it on every line, and in a narrow pane said "Thu" where the reader wanted the time.
 */
export function bubbleTimeLabel(date: AHDate): string {
  return date.format("h:mm A");
}

/**
 * The day a run of messages was sent, for the divider above them: "Today", "Yesterday", "Thu, Oct 15",
 * and "Thu, Oct 15, 2025" for another year's (#7082).
 *
 * `now` is passed in rather than read, so every divider in a list agrees about which day is today.
 */
export function conversationDayLabel(date: AHDate, now: Date): string {
  const then = date.asMoment.toDate();
  if (isSameDay(then, now)) return "Today";
  if (isSameDay(then, addDays(now, -1))) return "Yesterday";
  return then.getFullYear() === now.getFullYear() ? date.format("ddd, MMM D") : date.format("ddd, MMM D, YYYY");
}

/**
 * Whether a message dated `current` opens a new day after one dated `previous`, and so is drawn under a
 * divider. The first message in a list always is: the reader is owed the day of the first time they see.
 */
export function startsNewDay(previous: AHDate | undefined, current: AHDate): boolean {
  return !previous || !isSameDay(previous.asMoment.toDate(), current.asMoment.toDate());
}

/** The whole day, spelled out, for a screen reader that hears "Today" on a divider: "Tuesday, October 13, 2026". */
export function spokenDayLabel(date: AHDate): string {
  return date.format("dddd, MMMM D, YYYY");
}

/** The whole moment, spelled out, for a screen reader and the tooltip: "Monday, October 13, 2026 at 2:52 PM". */
export function spokenDateTimeLabel(date: AHDate): string {
  return date.format("dddd, MMMM D, YYYY [at] h:mm A");
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date: Date, days: number): Date {
  const shifted = new Date(date);
  shifted.setDate(shifted.getDate() + days);
  return shifted;
}

function isSameDay(left: Date, right: Date): boolean {
  return startOfDay(left).getTime() === startOfDay(right).getTime();
}
